import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api.js';
import { LeadStatusSelect } from '../components/LeadStatusBadge.js';
import { useAuth } from '../lib/auth-context.js';
import { useOrg } from '../lib/org-context.js';
import { OnboardingWizard } from '../components/OnboardingWizard.js';
import { OnboardingBanner } from '../components/OnboardingBanner.js';
import { Card, PageHeader, Button } from '../components/ui/index.js';

interface DashboardStats {
  new_this_week: number;
  needs_followup: number;
  in_trial: number;
  enrolled: number;
  total_leads: number;
  conversion_rate: number;
}

interface ActionItem {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string;
  created_at: string;
}

interface DashboardResponse {
  stats: DashboardStats;
  action_items: ActionItem[];
}

function daysAgo(dateStr: string): string {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function StatCard({
  value,
  label,
  borderColor,
}: {
  value: number;
  label: string;
  borderColor: string;
}) {
  return (
    <Card variant="elevated" size="compact">
      <div className={`border-l-4 ${borderColor} -ml-4 pl-4`}>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500 mt-1 font-medium">{label}</p>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const { orgId: authOrgId, orgName: authOrgName } = useAuth();
  const { viewingOrgId, viewingOrgName } = useOrg();
  const orgId = viewingOrgId ?? authOrgId;
  const orgName = viewingOrgName ?? authOrgName ?? '';

  const [data, setData] = useState<DashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Onboarding state
  const [showWizard, setShowWizard] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerKey, setBannerKey] = useState(0);
  const [, setOnboardingChecked] = useState(false);

  // Re-check banner when page gets focus (user returns from Settings)
  useEffect(() => {
    const handler = () => setBannerKey(k => k + 1);
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const result = await apiFetch<DashboardResponse>('/dashboard');
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      setError('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check onboarding status
  useEffect(() => {
    if (!orgId) {
      setOnboardingChecked(true);
      return;
    }

    apiFetch<{ settings?: Record<string, unknown> }>(`/orgs/${orgId}`)
      .then((org) => {
        const settings = org.settings ?? {};
        const step = settings.onboarding_step as string | undefined;
        const bannerDismissed = settings.onboarding_banner_dismissed as boolean | undefined;

        if (!step) {
          // Never started onboarding — show wizard
          setShowWizard(true);
        } else if (step === 'completed' && !bannerDismissed) {
          // Completed but banner not dismissed
          setShowBanner(true);
        }
      })
      .catch(() => {
        // Non-blocking — just skip onboarding check
      })
      .finally(() => setOnboardingChecked(true));
  }, [orgId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    setUpdatingId(leadId);
    try {
      await apiFetch(`/leads/${leadId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      await fetchDashboard();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-12 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-24" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-40 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-gray-900 font-medium">{error ?? 'Something went wrong'}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsLoading(true);
            fetchDashboard();
          }}
          className="mt-3"
        >
          Try again
        </Button>
      </div>
    );
  }

  const { stats, action_items } = data;

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" subtitle="Welcome back" />

      {/* Onboarding wizard modal */}
      {showWizard && orgId && (
        <OnboardingWizard
          orgId={orgId}
          orgName={orgName}
          onComplete={() => {
            setShowWizard(false);
            setShowBanner(true);
            fetchDashboard();
          }}
        />
      )}

      {/* Onboarding banner */}
      {showBanner && !showWizard && orgId && (
        <OnboardingBanner
          key={bannerKey}
          orgId={orgId}
          onDismiss={() => setShowBanner(false)}
        />
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard value={stats.new_this_week} label="New This Week" borderColor="border-l-blue-500" />
        <StatCard value={stats.needs_followup} label="Needs Follow-up" borderColor="border-l-yellow-500" />
        <StatCard value={stats.in_trial} label="In Trial" borderColor="border-l-purple-500" />
        <StatCard value={stats.enrolled} label="Enrolled" borderColor="border-l-green-500" />
      </div>

      {/* Action list */}
      <Card size="compact" className="!p-0">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Needs Attention</h2>
            {action_items.length > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                {action_items.length}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsLoading(true);
              fetchDashboard();
            }}
          >
            Refresh
          </Button>
        </div>

        {action_items.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 font-medium">All caught up!</p>
            <p className="text-xs text-gray-400 mt-1">No leads need attention right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {action_items.map((item) => (
              <div
                key={item.id}
                className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                {/* Name + contact */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {item.email}
                    {item.phone ? ` \u00b7 ${item.phone}` : ''}
                  </p>
                </div>

                {/* Time */}
                <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                  {daysAgo(item.created_at)}
                </span>

                {/* Status dropdown */}
                <div className="flex-shrink-0">
                  <LeadStatusSelect
                    status={item.status}
                    onChange={(s) => handleStatusChange(item.id, s)}
                    disabled={updatingId === item.id}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quick stats footer */}
      <div className="flex items-center justify-center gap-6 text-xs text-gray-400 py-2">
        <span>
          Conversion rate: <span className="font-medium text-gray-600">{stats.conversion_rate}%</span>
        </span>
        <span className="w-px h-3 bg-gray-200" />
        <span>
          Total leads: <span className="font-medium text-gray-600">{stats.total_leads}</span>
        </span>
      </div>
    </div>
  );
}
